import { SearchableSelectProps } from "@components:SearchableSelect/types";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import {
  Box,
  CircularProgress,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { groupBy } from "lodash";
import * as React from "react";
import { useMemo } from "react";

import { renderOptions } from "./SelectOptions";

export const SearchableSelect = <T,>(props: SearchableSelectProps<T>) => {
  const [searchText, setSearchText] = React.useState("");

  const displayedOptions = useMemo(() => {
    return props.options.filter((option) => {
      if (searchText === "") {
        return true;
      }
      return props.filterOptions
        ? props.filterOptions(option, searchText)
        : props
            .getOptionLabel(option)
            .toLowerCase()
            .includes(searchText.toLowerCase());
    });
  }, [props, searchText]);

  const groupedOptions = useMemo(
    () => groupBy(displayedOptions, props.groupBy),
    [displayedOptions, props.groupBy],
  );

  const renderText = useMemo(() => {
    const value = (props?.getRenderValue ?? props.getOptionLabel)(
      props.selectedOption,
    );
    if (value) {
      return value;
    }
    if (props?.noMatchText) {
      return props.noMatchText;
    }
    return "No Selection";
  }, [
    props.selectedOption,
    props.getOptionLabel,
    props.noMatchText,
    props.getRenderValue,
  ]);

  return (
    <Select
      displayEmpty
      disabled={props.isLoading}
      sx={{
        width: 270,
        height: 30,
        color: "#5F6368",
        fieldset: {
          borderWidth: "0",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderWidth: "0 !important",
          borderColor: "transparent !important",
        },
        backgroundColor: "#EEEDF3",
        ...(props.sx ? props.sx : {}),
      }}
      MenuProps={{
        autoFocus: false,
        PaperProps: {
          sx: {
            borderRadius: 2,
            maxHeight: 600,
            marginTop: 0.3,
            maxWidth: 250,
            overflow: "auto",
            "& .Mui-selected": {
              backgroundColor: "white !important",
            },
            backgroundColor: "#EEEDF3",
            ...(props.MenuProps?.sx ? props.MenuProps?.sx : {}),
          },
        },
      }}
      value={props.getOptionValue(props.selectedOption) || ""}
      onChange={(e) => {
        return props?.onChange?.(e?.target?.value as string);
      }}
      onClose={() => setSearchText("")}
      renderValue={() =>
        props.isLoading ? (
          <Box display="flex" alignItems="baseline">
            {props.loadingText && (
              <Box
                sx={{
                  marginRight: "0.5rem",
                }}
              >
                {props.loadingText}
              </Box>
            )}
            <CircularProgress size={15} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
          >
            {props?.RenderValueProps?.startAdornment}
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: "#5F6368",
                lineHeight: "26px",
              }}
            >
              {renderText}
            </Box>
          </Box>
        )
      }
    >
      <ListSubheader
        sx={{
          "&.MuiListSubheader-root": {
            position: "sticky",
            top: 10,
            backgroundColor: "#EEEDF3",
            zIndex: 100,
          },
          "& .MuiOutlinedInput-root": {
            padding: 0,
          },
        }}
      >
        <TextField
          sx={{
            "& .MuiOutlinedInput-root": {
              borderWidth: "0 !important",
              borderColor: "transparent !important",
              "&:hover fieldset": {
                borderWidth: "0 !important",
                borderColor: "transparent !important",
              },
            },
            fieldset: {
              borderWidth: 0,
              borderColor: "transparent !important",
            },
          }}
          size="small"
          autoFocus
          placeholder={props?.SearchProps?.placeholder ?? "Search data..."}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SVGInternalIcon
                    icon="botSearch"
                    sx={{ width: 18, height: 18 }}
                  />
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Escape") {
              e.stopPropagation();
            }
          }}
        />
      </ListSubheader>
      {props.groupBy
        ? Object.entries(groupedOptions).map(([groupLabel, options]) => {
            const lineBreak =
              groupLabel !==
              Object.keys(groupedOptions)[
                Object.keys(groupedOptions).length - 1
              ];
            return renderOptions(props, groupLabel, options, lineBreak);
          })
        : displayedOptions?.map((option, i) => (
            <MenuItem
              key={i}
              value={props.getOptionValue(option)}
              sx={{ height: 30 }}
              data-cy={`select-${props.getOptionLabel(option)}`}
            >
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#5F6368",
                  lineHeight: "26px",
                  paddingLeft: "10px",
                }}
                display="flex"
                alignItems="center"
              >
                <Tooltip
                  title={props.getOptionLabel(option)}
                  placement="right-end"
                >
                  <Box>{props.getOptionLabel(option)}</Box>
                </Tooltip>
              </Box>
            </MenuItem>
          ))}
    </Select>
  );
};
